import { Link } from "react-router-dom";
import type { Video } from "@/types/video";
import { useFavorites } from "@/hooks/useFavorites";
import { formatViews, relativeDate } from "@/utils/format";

interface VideoCardProps {
  video: Video;
}

export default function VideoCard({ video }: VideoCardProps) {
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(video.id);

  return (
    <article className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-all duration-300 hover:border-primary-200">
      <div className="relative">
        <Link
          to={`/video/${video.id}`}
          className="relative block aspect-video w-full overflow-hidden bg-background-100"
          aria-label={`观看 ${video.title}`}
        >
          <img
            src={video.cover}
            alt={`${video.title} 高清视频封面`}
            title={`${video.title} - 啪啪视频`}
            className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-transparent opacity-70 transition-opacity duration-300 group-hover:opacity-90"></div>
          <span className="absolute bottom-2 right-2 rounded-md bg-black/70 px-1.5 py-0.5 font-label text-xs text-white">
            {video.durationLabel}
          </span>
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-500/90 text-background-50 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
              <i className="ri-play-fill text-xl"></i>
            </span>
          </span>
        </Link>
        <button
          type="button"
          onClick={() => toggleFavorite(video.id)}
          aria-label={favorited ? "取消收藏" : "收藏视频"}
          aria-pressed={favorited}
          className={`absolute right-2 top-2 flex h-8 w-8 cursor-pointer items-center justify-center rounded-full backdrop-blur transition-colors duration-200 ${
            favorited ? "bg-accent-500 text-background-50" : "bg-black/45 text-white hover:bg-black/65"
          }`}
        >
          <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"}></i>
        </button>
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        <Link to={`/video/${video.id}`} className="block">
          <h3 className="line-clamp-2 text-sm font-semibold leading-snug text-foreground-900 transition-colors duration-200 group-hover:text-primary-600 md:text-base">
            {video.title}
          </h3>
        </Link>
        <div className="mt-auto flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground-500">
          <span className="inline-flex items-center gap-1">
            <i className="ri-user-3-line"></i>
            {video.author}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(video.views)}
          </span>
          <span className="inline-flex items-center gap-1">
            <i className="ri-time-line"></i>
            {relativeDate(video.uploadDate)}
          </span>
        </div>
      </div>
    </article>
  );
}